import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Modal, Badge, Table, Spinner, Form } from 'react-bootstrap';
import { FaUser, FaBook, FaClipboardList, FaChartBar, FaBell, FaSignOutAlt, FaChalkboardTeacher, FaUsers, FaQuestionCircle } from 'react-icons/fa';
import { getAuth, signOut } from 'firebase/auth';
import { getDatabase, ref, onValue, push } from 'firebase/database';
import { 
  getFirestore, collection, addDoc, query, orderBy, 
  getDocs, Timestamp, doc, updateDoc, deleteDoc, getDoc
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import AdminEnquiry from './AdminEnquiry'; // Fix import path

const TeacherDashboard = () => {
  const [teacherName, setTeacherName] = useState('');
  const [teacherData, setTeacherData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({});
  const [teachersList, setTeachersList] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    assignedClasses: [],
    exams: [],
    assignments: [],
    notices: [],
    enquiries: [] // Add this line
  });
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const db = getDatabase();
    const firestore = getFirestore();

    setTeacherName(user.displayName || user.email);

    // Fetch teacher profile from Firestore teachers collection
    const fetchTeacherProfile = async () => {
      try {
        const teacherDoc = await getDoc(doc(firestore, 'teachers', user.uid));
        if (teacherDoc.exists()) {
          const data = teacherDoc.data();
          setTeacherData(data);
          // After getting teacher data, fetch other data
          fetchTeacherSpecificData(data);
        } else {
          // If not found in Firestore, try Realtime DB
          fetchTeacherFromRealtimeDB();
        }
      } catch (error) {
        console.error("Error fetching teacher profile:", error);
        fetchTeacherFromRealtimeDB();
      }
    };

    const fetchTeacherFromRealtimeDB = () => {
      const teacherRef = ref(db, `teachers/${user.uid}`);
      onValue(teacherRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setTeacherData(data);
          fetchTeacherSpecificData(data);
        }
      }, { onlyOnce: true });
    };

    const fetchTeacherSpecificData = (teacherData) => {
      // Fetch assigned classes
      const classesRef = ref(db, 'teacherClasses');
      onValue(classesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const classesList = Object.entries(data)
            .map(([id, classData]) => ({
              id,
              ...classData
            }))
            .filter(classData => classData.teacherId === user.uid);
          setDashboardData(prev => ({ ...prev, assignedClasses: classesList }));
        }
      });

      // Fetch exams created by this teacher
      const examsRef = ref(db, 'exams');
      onValue(examsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const examsList = Object.entries(data)
            .map(([id, exam]) => ({
              id,
              ...exam
            }))
            .filter(exam => exam.teacherId === user.uid);
          setDashboardData(prev => ({ ...prev, exams: examsList }));
        }
      });

      // Fetch assignments created by this teacher
      const assignmentsRef = ref(db, 'assignments');
      onValue(assignmentsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const assignmentsList = Object.entries(data)
            .map(([id, assignment]) => ({
              id,
              ...assignment
            }))
            .filter(assignment => assignment.teacherId === user.uid);
          setDashboardData(prev => ({ ...prev, assignments: assignmentsList }));
        }
      });

      // Fetch notices for this teacher
      const noticesRef = ref(db, 'notices');
      onValue(noticesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const noticesList = Object.entries(data)
            .map(([id, notice]) => ({
              id,
              ...notice
            }))
            .filter(notice => 
              notice.teacherId === user.uid || 
              (teacherData && notice.department === teacherData.department) ||
              notice.forAll === true
            );
          setDashboardData(prev => ({ ...prev, notices: noticesList }));
        }
      });

      // Fetch enquiries for this teacher
      const enquiriesRef = ref(db, 'enquiries');
      onValue(enquiriesRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const enquiriesList = Object.entries(data).map(([id, enquiry]) => ({
            id,
            ...enquiry
          }));
          setDashboardData(prev => ({ ...prev, enquiries: enquiriesList }));
        }
      });
    };

    // Fetch all teachers from Realtime Database
    const fetchAllTeachers = () => {
      const teachersRef = ref(db, 'teachers');
      onValue(teachersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const teachersArray = Object.entries(data).map(([id, teacher]) => ({
            id,
            ...teacher
          }));
          setTeachersList(teachersArray);
        } else {
          setTeachersList([]);
        }
        setTeachersLoading(false);
      }, (error) => {
        console.error("Error fetching teachers:", error);
        setTeachersLoading(false);
      });
    };

    fetchTeacherProfile();
    fetchAllTeachers();
  }, [user, loading, navigate]);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth);
    window.location.href = '/';
  };

  // TeachersManagement component integrated
  const TeachersManagement = () => {
    return (
      <div>
        <h4 className="mb-4">Teachers List</h4>
        {teachersLoading ? (
          <div className="text-center">
            <Spinner animation="border" role="status" />
            <span className="ms-2">Loading teachers...</span>
          </div>
        ) : teachersList.length > 0 ? (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Subject</th>
                <th>Experience</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {teachersList.map((teacher, index) => (
                <tr key={teacher.id}>
                  <td>{index + 1}</td>
                  <td>{teacher.name || 'N/A'}</td>
                  <td>{teacher.email || 'N/A'}</td>
                  <td>{teacher.phone || 'N/A'}</td>
                  <td>{teacher.subject || 'N/A'}</td>
                  <td>{teacher.experience ? `${teacher.experience} years` : 'N/A'}</td>
                  <td>
                    <Badge bg={teacher.status === 'Active' ? 'success' : 'secondary'}>
                      {teacher.status || 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-muted">No teachers found in the database.</p>
        )}
      </div>
    );
  };

  const handleSubmitEnquiry = async (e) => {
    e.preventDefault();
    const form = e.target;
    
    try {
      const db = getFirestore();
      const enquiriesRef = collection(db, 'enquiries');
      
      await addDoc(enquiriesRef, {
        name: form.name.value,
        email: form.email.value,
        phone: form.phone.value,
        course: form.course.value,
        message: form.message.value,
        status: 'Pending',
        createdAt: Timestamp.now(),
        teacherId: user.uid
      });

      alert('Enquiry submitted successfully!');
      setShowModal(false);
      form.reset();
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      alert('Failed to submit enquiry. Please try again.');
    }
  };

  const quickLinks = [
    { 
      icon: <FaUser />, 
      label: 'View Profile', 
      color: '#1976d2', 
      info: teacherData ? (
        <div>
          <p><strong>Name:</strong> {teacherData.name}</p>
          <p><strong>Email:</strong> {teacherData.email}</p>
          <p><strong>Phone:</strong> {teacherData.phone}</p>
          <p><strong>Subject:</strong> {teacherData.subject}</p>
          <p><strong>Department:</strong> {teacherData.department}</p>
          <p><strong>Qualification:</strong> {teacherData.qualification}</p>
          <p><strong>Experience:</strong> {teacherData.experience} years</p>
          <p><strong>Joining Date:</strong> {teacherData.joiningDate}</p>
        </div>
      ) : 'Loading profile...'
    },
    { 
      icon: <FaChalkboardTeacher />, 
      label: 'My Classes', 
      color: '#43a047',
      info: (
        <div>
          <h4>Assigned Classes</h4>
          {dashboardData.assignedClasses.length > 0 ? (
            dashboardData.assignedClasses.map((cls, index) => (
              <Card key={index} className="mb-3">
                <Card.Body>
                  <Card.Title>Class {cls.class} - Section {cls.section}</Card.Title>
                  <Card.Text>
                    <Badge bg="info" className="me-2">{cls.subject}</Badge>
                    <div className="mt-2">Schedule: {cls.schedule}</div>
                  </Card.Text>
                </Card.Body>
              </Card>
            ))
          ) : (
            <p className="text-muted">No classes assigned yet.</p>
          )}
        </div>
      )
    },
    { 
      icon: <FaBook />, 
      label: 'My Exams', 
      color: '#fbc02d',
      info: (
        <div>
          <h4>Created Exams</h4>
          {dashboardData.exams.length > 0 ? (
            dashboardData.exams.map((exam, index) => (
              <Card key={index} className="mb-3">
                <Card.Body>
                  <Card.Title>{exam.title}</Card.Title>
                  <Card.Text>
                    <Badge bg="info" className="me-2">Class {exam.class}</Badge>
                    <Badge bg="warning" className="me-2">{exam.subject}</Badge>
                    <div className="mt-2">Date: {exam.date}</div>
                    <div>Duration: {exam.duration} minutes</div>
                  </Card.Text>
                </Card.Body>
              </Card>
            ))
          ) : (
            <p className="text-muted">No exams created yet.</p>
          )}
        </div>
      )
    },
    { 
      icon: <FaClipboardList />, 
      label: 'Assignments', 
      color: '#d32f2f',
      info: (
        <div>
          <h4>Created Assignments</h4>
          {dashboardData.assignments.length > 0 ? (
            dashboardData.assignments.map((assignment, index) => (
              <Card key={index} className="mb-3">
                <Card.Body>
                  <Card.Title>{assignment.title}</Card.Title>
                  <Card.Text>
                    <Badge bg="info" className="me-2">Class {assignment.class}</Badge>
                    <Badge bg="warning" className="me-2">{assignment.subject}</Badge>
                    <div className="mt-2">Due Date: {assignment.dueDate}</div>
                  </Card.Text>
                </Card.Body>
              </Card>
            ))
          ) : (
            <p className="text-muted">No assignments created yet.</p>
          )}
        </div>
      )
    },
    { 
      icon: <FaBell />, 
      label: 'Notices', 
      color: '#7b1fa2',
      info: (
        <div>
          <h4>Latest Notices</h4>
          {dashboardData.notices.length > 0 ? (
            dashboardData.notices.map((notice, index) => (
              <Card key={index} className="mb-3">
                <Card.Body>
                  <Card.Title>{notice.title}</Card.Title>
                  <Card.Text>
                    <Badge bg="info" className="me-2">{notice.category}</Badge>
                    <div className="mt-2">{notice.description}</div>
                    <small className="text-muted">Posted on: {notice.date}</small>
                  </Card.Text>
                </Card.Body>
              </Card>
            ))
          ) : (
            <p className="text-muted">No notices available at the moment.</p>
          )}
        </div>
      )
    },
    { 
      icon: <FaUsers />, 
      label: 'Teachers List', 
      color: '#ff6f00',
      info: <TeachersManagement />
    },
    { 
      icon: <FaQuestionCircle />, 
      label: 'Add New Enquiry', 
      color: '#009688',
      info: (
        <div>
          <h4>Add New Enquiry</h4>
          <Form onSubmit={handleSubmitEnquiry}>
            <Form.Group className="mb-3">
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                name="name"
                required
                placeholder="Enter your name"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="email"
                name="email"
                required
                placeholder="Enter your email"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                required
                placeholder="Enter your phone number"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Course <span className="text-danger">*</span></Form.Label>
              <Form.Select name="course" required>
                <option value="">Select a course</option>
                <option value="ADCA">ADCA</option>
                <option value="O Level">O Level</option>
                <option value="CCC">CCC</option>
                <option value="DCA">DCA</option>
                <option value="Tally">Tally</option>
                <option value="Web Design">Web Design</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                name="message"
                rows={4}
                required
                placeholder="Enter your message"
              />
            </Form.Group>

            <div className="d-grid gap-2">
              <Button type="submit" variant="primary">
                Submit Enquiry
              </Button>
            </div>
          </Form>
        </div>
      )
    }
  ];

  const handleCardClick = (link) => {
    setModalContent(link);
    setShowModal(true);
  };

  return (
    <div className="teacher-dashboard-container" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e3f0ff 0%, #f9f9f9 100%)', paddingBottom: 40 }}>
      <div className="dashboard-header" style={{ background: 'linear-gradient(90deg, #1976d2 60%, #43a047 100%)', borderRadius: 18, boxShadow: '0 4px 24px rgba(25, 118, 210, 0.08)', padding: '2.5rem 2rem 2rem 2rem', marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ color: '#fff', fontWeight: 700, fontSize: 36, marginBottom: 8, letterSpacing: 1 }}>
              Welcome, {teacherData?.name || teacherName || 'Teacher'}!
            </h1>
            <p className="dashboard-subtitle" style={{ color: '#e3f0ff', fontSize: 18, marginBottom: 0 }}>
              {teacherData?.subject || 'Subject'} Teacher | {teacherData?.department || 'Department'} Department
            </p>
          </div>
          <Button variant="danger" onClick={handleLogout} style={{ height: 44, display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 18, borderRadius: 12, boxShadow: '0 2px 8px rgba(211,47,47,0.08)' }}>
            <FaSignOutAlt style={{ fontSize: 22 }} /> Logout
          </Button>
        </div>
      </div>

      <Row className="g-4 mt-2" style={{ justifyContent: 'center' }}>
        {quickLinks.map((link, idx) => (
          <Col xs={12} sm={6} md={4} lg={3} key={idx}>
            <Card 
              className="dashboard-card" 
              style={{ 
                borderTop: `5px solid ${link.color}`, 
                borderRadius: 18, 
                boxShadow: '0 4px 24px rgba(25, 118, 210, 0.08)', 
                transition: 'transform 0.2s, box-shadow 0.2s', 
                cursor: 'pointer', 
                minHeight: 220 
              }}
              onClick={() => handleCardClick(link)}
              onMouseOver={e => { 
                e.currentTarget.style.transform = 'translateY(-8px) scale(1.04)'; 
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(25, 118, 210, 0.16)'; 
              }}
              onMouseOut={e => { 
                e.currentTarget.style.transform = 'none'; 
                e.currentTarget.style.boxShadow = '0 4px 24px rgba(25, 118, 210, 0.08)'; 
              }}
            >
              <div 
                className="dashboard-icon" 
                style={{ 
                  color: link.color, 
                  fontSize: 44, 
                  margin: '32px auto 0 auto', 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  width: 64, 
                  height: 64, 
                  background: '#fff', 
                  borderRadius: '50%', 
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)' 
                }}
              >
                {link.icon}
              </div>
              <Card.Body style={{ textAlign: 'center', paddingTop: 18 }}>
                <Card.Title style={{ fontWeight: 700, fontSize: 20, color: '#222' }}>{link.label}</Card.Title>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Modern Modal Popup */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        centered 
        size="lg"
        className="dashboard-modal"
        scrollable
      >
        <Modal.Header 
          closeButton 
          style={{ 
            background: modalContent.color, 
            color: '#fff', 
            borderTopLeftRadius: 12, 
            borderTopRightRadius: 12 
          }}
        >
          <Modal.Title style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 32 }}>{modalContent.icon}</span> {modalContent.label}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '2.5rem', background: '#f7f9fa', borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }}>
          <div style={{ fontSize: '1.18rem', color: '#333', minHeight: 80 }}>
            {modalContent.info}
          </div>
        </Modal.Body>
      </Modal>

      <style>{`
        @media (max-width: 900px) {
          .dashboard-header { padding: 1.5rem 1rem 1rem 1rem !important; }
          .dashboard-card { min-height: 180px !important; }
        }
      `}</style>
    </div>
  );
};

export default TeacherDashboard;